/**
 * Footnotes as a second source of document text (AIVA-9042, subtask 6).
 *
 * Every defined-terms sweep read `context.document.body.paragraphs`, which is
 * the main story only: a footnote's text is outside it. So a term defined in a
 * footnote was invisible, a term used only in one looked unused, and nothing
 * inside a footnote could ever be flagged. `Body.footnotes` (WordApi 1.5)
 * reaches them.
 *
 * ## The location convention
 *
 * The server keys a finding's location by `(matchText, occurrenceIndex)` alone.
 * Rather than widen that key, a footnote occurrence is numbered by continuing
 * the main text's count: the main text's occurrences come first, in order,
 * then each footnote's, footnote by footnote. Occurrence 5 of "X" in a document
 * whose main text holds 3 is the second one in the footnotes. Every location in
 * the main text keeps exactly the index it had before footnotes were read, and
 * the anchor step only looks in the footnotes for an index the main text has no
 * hit for (see `anchorFindings` in docReview.ts).
 *
 * Fails soft throughout: a host without the API (Word 2016, older Word on the
 * web), or a read that throws, reads as "no footnotes". A review that cannot
 * see footnotes is still worth running, and is exactly what ran before.
 */
import { logOfficeError } from "../../../../src/telemetry";

/** `Body.footnotes` / `NoteItem` — WordApi 1.5. */
export function supportsFootnotes(): boolean {
  try {
    return Office.context.requirements.isSetSupported("WordApi", "1.5");
  } catch {
    return false;
  }
}

/** Each footnote, in document order — its `body` (the note's text) and its
 *  `reference` (the number mark in the main text). Empty when the host has no
 *  footnote API, the document has none, or the read throws. */
export async function loadFootnotes(context: Word.RequestContext): Promise<Word.NoteItem[]> {
  if (!supportsFootnotes()) return [];
  try {
    const notes = context.document.body.footnotes;
    notes.load("items");
    await context.sync();
    return notes.items;
  } catch (err) {
    logOfficeError(err, "word-docreview-footnotes");
    return [];
  }
}

/** The body of each footnote, in document order — a live proxy the caller can
 *  search or read. Empty as `loadFootnotes` is. */
export async function loadFootnoteBodies(context: Word.RequestContext): Promise<Word.Body[]> {
  return (await loadFootnotes(context)).map((note) => note.body);
}

/** Each footnote's text, in document order — one string per footnote, with a
 *  multi-paragraph footnote's paragraphs joined by Word's own "\r". Empty when
 *  footnotes cannot be read (see `loadFootnoteBodies`). */
export async function loadFootnoteTexts(context: Word.RequestContext): Promise<string[]> {
  const bodies = await loadFootnoteBodies(context);
  if (bodies.length === 0) return [];
  try {
    for (const body of bodies) body.load("text");
    await context.sync();
    return bodies.map((body) => body.text ?? "");
  } catch (err) {
    logOfficeError(err, "word-docreview-footnote-text");
    return [];
  }
}

/** The label a finding raised inside footnote `n` (1-based) carries, so the
 *  operator can tell it from a main-text finding. */
export function footnoteLabel(footnote: number): string {
  return `(in footnote ${footnote})`;
}

/** What a footnote finding adds to its `fix_params`: where it came from, for
 *  anything that needs to tell the two apart without parsing the label. */
export function footnoteSource(footnote: number): { source: "footnote"; footnote: number } {
  return { source: "footnote", footnote };
}
